import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { DebugInstrumentor } from '@/components/general/DebugInstrumentor';
import { ErrorBoundary } from '@/components/general/ErrorBoundary';
import { Home } from '@/components/general/Home';
import { ProtectedLayout } from '@/components/routing/ProtectedLayout';
import { DashboardSkeleton } from '@/features/dashboard/components/DashboardSkeleton';
import { FAQSkeleton } from '@/features/faq/FAQSkeleton';
// WHY: GoalsSkeleton is lightweight markup only — negligible bundle cost even when Goals is flagged off.
// The meaningful bundle savings come from the conditional lazy() for GoalsPage and GoalDiscoveryPage.
import { GoalsSkeleton } from '@/features/goals/GoalsSkeleton';
import { LearningSkeleton } from '@/features/learning/components/LearningSkeleton';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { OnboardingSkeleton } from '@/features/onboarding/components/OnboardingSkeleton';
import { ProfileSkeleton } from '@/features/profile/components/ProfileSkeleton';
import { SessionsSkeleton } from '@/features/sessions/components/SessionsSkeleton';
import { TasksSkeleton } from '@/features/tasks/components/TasksSkeleton';
import { AppShellSkeleton } from '@/layouts/MainLayout/AppShellSkeleton';
import { ContentRouteFallback } from './content-route-fallback';
import { featureFlags } from './featureFlags';

// --- Lazy Loads ---

const MainLayout = lazy(() => import('@/layouts/MainLayout').then((module) => ({ default: module.MainLayout })));

const OnboardingPage = lazy(() =>
  import('@/features/onboarding').then((module) => ({ default: module.OnboardingPage }))
);

const PrevMeetingRoom = lazy(() =>
  import('@/features/sessions/components/PrevMeetingRoom').then((module) => ({ default: module.PrevMeetingRoom }))
);

const FinalAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/FinalAssessmentSurvey').then((module) => ({
    default: module.FinalAssessmentSurvey
  }))
);
const MidCohortAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/MidCohortAssessmentSurvey').then((module) => ({
    default: module.MidCohortAssessmentSurvey
  }))
);
const PilotGroupAssessment = lazy(() =>
  import('@/features/tasks/components/PilotGroupAssessment').then((module) => ({
    default: module.PilotGroupAssessment
  }))
);
const OneOnOneFinalAssessment = lazy(() =>
  import('@/features/tasks/components/OneOnOneFinalAssessment').then((module) => ({
    default: module.OneOnOneFinalAssessment
  }))
);
const CoachFitAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/CoachFitAssessmentSurvey').then((module) => ({
    default: module.CoachFitAssessmentSurvey
  }))
);
const FirstSessionEvaluation = lazy(() =>
  import('@/features/tasks/components/FirstSessionEvaluation').then((module) => ({
    default: module.FirstSessionEvaluation
  }))
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const ProfilePage = lazy(() =>
  import('@/features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const SessionsPage = lazy(() =>
  import('@/features/sessions/SessionsPage').then((module) => ({ default: module.SessionsPage }))
);
const LearningPage = lazy(() =>
  import('@/features/learning/LearningPage').then((module) => ({ default: module.LearningPage }))
);
const TasksPage = lazy(() => import('@/features/tasks/TasksPage').then((module) => ({ default: module.TasksPage })));

const GoalsPage = featureFlags.goals
  ? lazy(() => import('@/features/goals').then((module) => ({ default: module.GoalsPage })))
  : null;

const GoalDiscoveryPage = featureFlags.goals
  ? lazy(() => import('@/features/goals').then((module) => ({ default: module.GoalDiscoveryPage })))
  : null;

const FAQPage = lazy(() => import('@/features/faq/FAQPage').then((module) => ({ default: module.FAQPage })));

const renderLazyRoute = (element: ReactNode, fallback: ReactNode = <AppShellSkeleton />) => (
  <Suspense fallback={fallback}>{element}</Suspense>
);

interface AppRouterProps {
  isAuthenticated: boolean;
}

/**
 * Orchestrates the application's routing table and global UI elements like Toast.
 */
export function AppRouter({ isAuthenticated }: AppRouterProps) {
  return (
    <BrowserRouter future={{ v7_startTransition: true }}>
      {import.meta.env.DEV && <DebugInstrumentor />}
      <ErrorBoundary name="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/home" replace /> : <Home />} />
          <Route path="/meetings/:meetingId" element={renderLazyRoute(<PrevMeetingRoom />, <AppShellSkeleton />)} />
          <Route path="/:code/all" element={renderLazyRoute(<OnboardingPage />, <OnboardingSkeleton />)} />

          {/* Assessment routes — standalone (no MainLayout), use content-only fallback */}
          <Route
            path="/first-session-evaluation/:userId/:taskId/:groupId"
            element={renderLazyRoute(<FirstSessionEvaluation />, <ContentRouteFallback />)}
          />
          <Route
            path="/final-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<FinalAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/pilot-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<PilotGroupAssessment />, <ContentRouteFallback />)}
          />
          <Route
            path="/mid-cohort-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<MidCohortAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/coach-fit-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<CoachFitAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/one-on-one-final/:userId/:taskId/:groupId"
            element={renderLazyRoute(<OneOnOneFinalAssessment />, <ContentRouteFallback />)}
          />

          {/* Protected routes */}
          <Route element={<ProtectedLayout />}>
            {featureFlags.goals && GoalDiscoveryPage && (
              <Route
                path="/goals/discovery"
                element={renderLazyRoute(<GoalDiscoveryPage />, <ContentRouteFallback />)}
              />
            )}
            {/* MainLayout — full AppShellSkeleton while layout chunk loads */}
            <Route element={renderLazyRoute(<MainLayout />)}>
              {/* Page routes — per-page skeleton fallbacks (sidebar/header already mounted) */}
              <Route path="/home" element={renderLazyRoute(<DashboardPage />, <DashboardSkeleton />)} />
              <Route path="/profile" element={renderLazyRoute(<ProfilePage />, <ProfileSkeleton />)} />
              <Route path="/sessions" element={renderLazyRoute(<SessionsPage />, <SessionsSkeleton />)} />
              <Route path="/resources" element={renderLazyRoute(<LearningPage />, <LearningSkeleton />)} />
              <Route path="/tasks" element={renderLazyRoute(<TasksPage />, <TasksSkeleton />)} />
              {featureFlags.goals && GoalsPage && (
                <Route path="/goals" element={renderLazyRoute(<GoalsPage />, <GoalsSkeleton />)} />
              )}
              <Route path="/faq" element={renderLazyRoute(<FAQPage />, <FAQSkeleton />)} />
            </Route>
          </Route>

          {/* Fallback for unknown routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <Toaster position="bottom-right" />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
